import React from 'react';
import { useTranslation } from 'react-i18next';
import { filterNullish } from '@modernhealth/filter-nullish';
import styled from '@emotion/styled';
import {
    HeadingText40,
    HeadingText50,
    HeadingText60,
    BodyTextMedium,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';

import { InternalLinkButton } from 'src/shared/components/InternalLinkButton';
import pathwaysBackground from 'src/modules/pathways/assets/pathways-background.svg';
import { Grid } from 'src/shared/components/Grid';
import { PageContentContainer } from 'src/modules/pathways/components/PageLayout';

import { usePathwaysQuery } from '../documents/pathways.generated';
import { PathwayFragment } from '../documents/fragments.generated';

export const PathwaysPage = () => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const { data } = usePathwaysQuery();

    if (!data?.pathways) {
        return null;
    }

    const { pathways } = data;

    const renderBtnText = (pathway: PathwayFragment) => {
        return pathway.memberPathway
            ? 'pathways:pathways_page.pathways.continue'
            : 'pathways:pathways_page.pathways.explore';
    };

    return (
        <>
            <Header>
                <PageContentContainer
                    width={1440}
                    css={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <HeaderTextContainer>
                        <HeadingText40 fontColor="blue20">
                            {t('pathways:pathways_page.header.title')}
                        </HeadingText40>
                        <HeadingText60 fontColor="white">
                            {t('pathways:pathways_page.header.subtitle')}
                        </HeadingText60>
                        <BodyTextMedium marginBottom={20} fontColor="white">
                            {t('pathways:pathways_page.header.description')}
                        </BodyTextMedium>

                        <InternalLinkButton
                            onClick={() =>
                                analytics.trackEvent(
                                    EventLexicon.clickPathwaysLearnMore({
                                        destination: '/pathways/about',
                                        translationKey: 'pathways:pathways_page.header.button',
                                    }),
                                )
                            }
                            variant="secondary"
                            rounded
                            to="/pathways/about"
                            size="medium"
                        >
                            {t('pathways:pathways_page.header.button')}
                        </InternalLinkButton>
                    </HeaderTextContainer>
                </PageContentContainer>
                <PathwaysBackground />
            </Header>
            <PageContentContainer width={1440} css={{ paddingBottom: 80 }}>
                <HeadingText40 marginBottom={32}>
                    {t('pathways:pathways_page.pathways.title')}
                </HeadingText40>

                <Grid minItemWidth={360} columnGap={24} rowGap={24} maxColumns={2}>
                    {filterNullish(pathways).map(pathway => (
                        <div key={pathway.uuid}>
                            <PathwayCardImage
                                coverImage={
                                    pathway.metadata?.global_landing_page?.what_youll_work_on
                                        ?.background_image_web
                                }
                            />
                            <PathwayCardTextContainer>
                                <HeadingText50>{pathway.title}</HeadingText50>
                                <BodyTextMedium marginBottom={20} fontColor="gray85">
                                    {pathway.description}
                                </BodyTextMedium>
                                <GoalsContainer>
                                    {pathway.metadata?.global_landing_page?.what_youll_work_on?.goals?.map(
                                        goal => {
                                            if (!goal) return null;
                                            return (
                                                <GoalContainer key={goal.text}>
                                                    <img
                                                        src={goal.icon}
                                                        width="24"
                                                        height="24"
                                                        alt=""
                                                    />
                                                    <BodyTextMedium
                                                        marginLeft={8}
                                                        fontColor="gray85"
                                                    >
                                                        {goal.text}
                                                    </BodyTextMedium>
                                                </GoalContainer>
                                            );
                                        },
                                    )}
                                </GoalsContainer>
                                <InternalLinkButton
                                    onClick={() =>
                                        analytics.trackEvent(
                                            EventLexicon.clickExplorePathways({
                                                destination: `/pathways/${pathway.uuid}`,
                                                translationKey: renderBtnText(pathway),
                                                pathwayUuid: pathway.uuid,
                                            }),
                                        )
                                    }
                                    key={pathway.uuid}
                                    to={`/pathways/${pathway.uuid}`}
                                    variant={!pathway.memberPathway ? 'primary' : 'secondary'}
                                    size="small"
                                    rounded
                                >
                                    {t(renderBtnText(pathway))}
                                </InternalLinkButton>
                            </PathwayCardTextContainer>
                        </div>
                    ))}
                </Grid>
            </PageContentContainer>
        </>
    );
};

const Header = styled.div({
    height: '470px',
    background: `linear-gradient(to bottom right, ${colors.blue55}, ${colors.blue65})`,
    position: 'relative',
    marginBottom: '80px',
    overflow: 'hidden',
});

const HeaderTextContainer = styled.div({
    maxWidth: '360px',
});

const PathwaysBackground = styled.div({
    width: '650px',
    height: '100%',
    backgroundImage: `url('${pathwaysBackground}')`,
    backgroundRepeat: 'no-repeat',
    position: 'absolute',
    left: '50%',
    top: 15,
    borderRadius: '32px',
});

const PathwayCardImage = styled.div<{ coverImage: string }>(props => ({
    height: '360px',
    borderRadius: '8px 8px 0px 0px',
    backgroundImage: `url(${props.coverImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
}));

const PathwayCardTextContainer = styled.div({
    padding: '36px',
    borderRadius: '0 0 8px 8px',
    backgroundColor: colors.gray05,
});

const GoalsContainer = styled.div({
    marginBottom: '20px',
});

const GoalContainer = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '8px',
});
